import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// withCredentials: true → envía/recibe cookies (incluido el JWT httpOnly).
const api = axios.create({ baseURL: API, withCredentials: true });

// Lee una cookie del navegador (no httpOnly). Se usa para el CSRF token.
function readCookie(name) {
  if (typeof document === "undefined") return "";
  const prefix = `${name}=`;
  const parts = document.cookie ? document.cookie.split(";") : [];
  for (const raw of parts) {
    const c = raw.trim();
    if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length));
  }
  return "";
}

// Token CSRF en memoria: respaldo cuando `document.cookie` no expone la cookie
// (p.ej. API servida desde otro dominio o cookie caducada). Se obtiene de
// GET /auth/csrf, que devuelve exactamente el valor que el servidor ve en la
// cookie (patrón double-submit), de modo que header y cookie siempre coinciden.
let csrfMemory = "";
let csrfRefreshing = null;

export async function refreshCsrfToken() {
  if (!csrfRefreshing) {
    csrfRefreshing = axios
      .get(`${API}/auth/csrf`, { withCredentials: true })
      .then((r) => { csrfMemory = r.data?.csrf_token || ""; return csrfMemory; })
      .catch(() => "")
      .finally(() => { csrfRefreshing = null; });
  }
  return csrfRefreshing;
}

function currentCsrf() {
  return readCookie("csrf_token") || csrfMemory;
}

api.interceptors.request.use(async (config) => {
  // Añade automáticamente el header X-CSRF-Token en métodos mutables.
  const method = (config.method || "get").toLowerCase();
  if (!["get", "head", "options"].includes(method) && !(config.url || "").includes("/auth/login")) {
    let csrf = currentCsrf();
    if (!csrf) csrf = await refreshCsrfToken();
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

// Reintento único si el servidor rechaza el CSRF (token caducado/desincronizado):
// se vuelve a pedir el token vigente al backend y se repite la misma petición.
api.interceptors.response.use(undefined, async (error) => {
  const cfg = error?.config;
  const detail = error?.response?.data?.detail;
  const isCsrf = error?.response?.status === 403 && typeof detail === "string" && /csrf/i.test(detail);
  if (isCsrf && cfg && !cfg._csrfRetried) {
    cfg._csrfRetried = true;
    const fresh = await refreshCsrfToken();
    if (fresh) {
      cfg.headers = { ...(cfg.headers || {}), "X-CSRF-Token": fresh };
      return api.request(cfg);
    }
  }
  return Promise.reject(error);
});

export default api;

/** Headers + opciones recomendadas para llamadas fetch() directas
 * (descargas de PDF, exports XLSX, uploads multipart) que no pasan por axios. */
export function fetchAuthOpts(extra = {}) {
  const headers = { ...(extra.headers || {}) };
  const method = (extra.method || "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = currentCsrf();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }
  return { credentials: "include", ...extra, headers };
}

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Algo salió mal";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return JSON.stringify(detail);
}

export const STATUS_LABELS = {
  recibido: "Recibido",
  en_diagnostico: "En diagnóstico",
  en_reparacion: "En reparación",
  listo_para_entregar: "Listo para entregar",
  listo_sin_reparacion: "Listo (sin reparación)",
  entregado: "Entregado",
  cancelado: "Cancelado",
};

export const STATUS_ORDER = [
  "recibido",
  "en_diagnostico",
  "en_reparacion",
  "listo_para_entregar",
  "listo_sin_reparacion",
  "entregado",
  "cancelado",
];

export const STATUS_STYLE = {
  recibido: "bg-neutral-100 text-neutral-700 border-neutral-300",
  en_diagnostico: "bg-amber-50 text-amber-800 border-amber-300",
  en_reparacion: "bg-blue-50 text-blue-800 border-blue-300",
  listo_para_entregar: "bg-emerald-50 text-emerald-800 border-emerald-300",
  listo_sin_reparacion: "bg-orange-50 text-orange-800 border-orange-300",
  entregado: "bg-green-100 text-green-900 border-green-400",
  cancelado: "bg-red-50 text-red-800 border-red-300",
};
